/**
 * History Tab Phase 2 - Visual Timeline & Grouping
 * 
 * New Features:
 * - Visual timeline view with branching
 * - Activity heatmap
 * - Collapsible grouping (by date, user, type)
 * - Bulk operations
 * - Activity metrics dashboard
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Collapse,
  Button,
  Checkbox,
  Tooltip,
  LinearProgress,
  Divider,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  TableChart as TableIcon,
  CalendarToday as HeatmapIcon,
  BarChart as MetricsIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  DateRange as DateIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  GetApp as ExportIcon,
} from '@mui/icons-material';
import EnhancedHistoryTab, { HistoryEntry } from './EnhancedHistoryTab';

const USCIS_BLUE = '#003366';
const USCIS_RED = '#B31B1B';

interface HistoryTabPhase2Props {
  entries: HistoryEntry[];
  title?: string;
  onBulkDelete?: (ids: string[]) => void;
  onBulkArchive?: (ids: string[]) => void;
  onBulkExport?: (ids: string[]) => void;
}

type ViewMode = 'table' | 'timeline' | 'heatmap' | 'metrics';
type GroupBy = 'none' | 'date' | 'user' | 'type';

const HistoryTabPhase2: React.FC<HistoryTabPhase2Props> = ({
  entries,
  title = 'History',
  onBulkDelete,
  onBulkArchive,
  onBulkExport,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Group entries
  const groupedEntries = useMemo(() => {
    if (groupBy === 'none') return { 'All Entries': entries };

    const groups: Record<string, HistoryEntry[]> = {};
    entries.forEach(entry => {
      let key = '';
      switch (groupBy) {
        case 'date':
          key = entry.date.toLocaleDateString();
          break;
        case 'user':
          key = entry.user;
          break;
        case 'type':
          key = entry.actionType;
          break;
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(entry);
    });
    return groups;
  }, [entries, groupBy]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const byType: Record<string, number> = {};
    const byUser: Record<string, number> = {};
    const byDate: Record<string, number> = {};

    entries.forEach(entry => {
      byType[entry.actionType] = (byType[entry.actionType] || 0) + 1;
      byUser[entry.user] = (byUser[entry.user] || 0) + 1;
      const dateKey = entry.date.toLocaleDateString();
      byDate[dateKey] = (byDate[dateKey] || 0) + 1;
    });

    return { byType, byUser, byDate, total: entries.length };
  }, [entries]);

  // Heatmap data (last 30 days)
  const heatmapData = useMemo(() => {
    const data: { date: string; count: number }[] = [];
    const today = new Date();
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateKey = date.toLocaleDateString();
      const count = metrics.byDate[dateKey] || 0;
      data.push({ date: dateKey, count });
    }
    
    return data;
  }, [metrics]);

  const toggleGroup = (group: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(group)) {
      newExpanded.delete(group);
    } else {
      newExpanded.add(group);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    setSelectedIds(new Set(entries.map(e => e.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  // Render timeline view
  const renderTimeline = () => (
    <Box sx={{ p: 3 }}>
      {Object.entries(groupedEntries).map(([group, groupEntries]) => (
        <Box key={group} sx={{ mb: 4 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }}
            onClick={() => toggleGroup(group)}
          >
            <IconButton size="small">
              {expandedGroups.has(group) ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
            <Typography variant="h6" sx={{ ml: 1, color: USCIS_BLUE }}>
              {group}
            </Typography>
            <Chip label={groupEntries.length} size="small" sx={{ ml: 2 }} />
          </Box>

          <Collapse in={expandedGroups.has(group) || groupBy === 'none'}>
            <Box sx={{ position: 'relative', pl: 4 }}>
              {/* Timeline line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 20,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  bgcolor: '#e0e0e0',
                }}
              />

              {groupEntries.map((entry, index) => (
                <Box key={entry.id} sx={{ position: 'relative', mb: 3 }}>
                  {/* Timeline dot */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: -24,
                      top: 8,
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: USCIS_BLUE,
                      border: '2px solid white',
                      boxShadow: 1,
                    }}
                  />

                  <Card sx={{ ml: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Checkbox
                          checked={selectedIds.has(entry.id)}
                          onChange={() => toggleSelection(entry.id)}
                          size="small"
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                          {entry.action}
                        </Typography>
                        <Chip label={entry.actionType} size="small" sx={{ mr: 1 }} />
                        <Typography variant="caption" color="text.secondary">
                          {entry.date.toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {entry.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        <Typography variant="caption">{entry.user}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      ))}
    </Box>
  );

  // Render heatmap
  const renderHeatmap = () => {
    const maxCount = Math.max(...heatmapData.map(d => d.count), 1);

    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, color: USCIS_BLUE }}>
          Activity Heatmap - Last 30 Days
        </Typography>
        <Grid container spacing={1}>
          {heatmapData.map((day, index) => {
            const intensity = day.count / maxCount;
            const color = intensity === 0 ? '#f5f5f5' : 
                         intensity < 0.33 ? '#c8e6c9' :
                         intensity < 0.66 ? '#66bb6a' : '#2e7d32';

            return (
              <Grid item key={index}>
                <Tooltip title={`${day.date}: ${day.count} activities`}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      bgcolor: color,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 },
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: 10 }}>
                      {day.count || ''}
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>Activity by Type</Typography>
          {Object.entries(metrics.byType).map(([type, count]) => (
            <Box key={type} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{type}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{count}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(count / metrics.total) * 100}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // Render metrics dashboard
  const renderMetrics = () => (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary">Total Activities</Typography>
              <Typography variant="h3" sx={{ color: USCIS_BLUE, fontWeight: 'bold' }}>
                {metrics.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary">Active Users</Typography>
              <Typography variant="h3" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                {Object.keys(metrics.byUser).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary">Action Types</Typography>
              <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                {Object.keys(metrics.byType).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Top Contributors</Typography>
              {Object.entries(metrics.byUser)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([user, count]) => (
                  <Box key={user} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{user}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{count}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(count / metrics.total) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Activity Distribution</Typography>
              {Object.entries(metrics.byType).map(([type, count]) => (
                <Box key={type} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{type}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {((count / metrics.total) * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(count / metrics.total) * 100}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box>
      {/* Header with view mode tabs */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: USCIS_BLUE }}>
            {title}
          </Typography>
          
          {selectedIds.size > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip label={`${selectedIds.size} selected`} color="primary" />
              {onBulkDelete && (
                <Button
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => onBulkDelete(Array.from(selectedIds))}
                  color="error"
                >
                  Delete
                </Button>
              )}
              {onBulkArchive && (
                <Button
                  size="small"
                  startIcon={<ArchiveIcon />}
                  onClick={() => onBulkArchive(Array.from(selectedIds))}
                >
                  Archive
                </Button>
              )}
              {onBulkExport && (
                <Button
                  size="small"
                  startIcon={<ExportIcon />}
                  onClick={() => onBulkExport(Array.from(selectedIds))}
                >
                  Export
                </Button>
              )}
              <Button size="small" onClick={deselectAll}>Clear</Button>
            </Box>
          )}
        </Box>

        <Tabs value={viewMode} onChange={(_, v) => setViewMode(v)} sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Tab icon={<TableIcon />} label="Table" value="table" />
          <Tab icon={<TimelineIcon />} label="Timeline" value="timeline" />
          <Tab icon={<HeatmapIcon />} label="Heatmap" value="heatmap" />
          <Tab icon={<MetricsIcon />} label="Metrics" value="metrics" />
        </Tabs>
      </Paper>

      {/* Grouping controls for timeline view */}
      {viewMode === 'timeline' && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Group By:</Typography>
            <Button
              size="small"
              variant={groupBy === 'none' ? 'contained' : 'outlined'}
              onClick={() => setGroupBy('none')}
            >
              None
            </Button>
            <Button
              size="small"
              variant={groupBy === 'date' ? 'contained' : 'outlined'}
              startIcon={<DateIcon />}
              onClick={() => setGroupBy('date')}
            >
              Date
            </Button>
            <Button
              size="small"
              variant={groupBy === 'user' ? 'contained' : 'outlined'}
              startIcon={<PersonIcon />}
              onClick={() => setGroupBy('user')}
            >
              User
            </Button>
            <Button
              size="small"
              variant={groupBy === 'type' ? 'contained' : 'outlined'}
              startIcon={<CategoryIcon />}
              onClick={() => setGroupBy('type')}
            >
              Type
            </Button>
            {selectedIds.size === 0 && (
              <Button size="small" onClick={selectAll} sx={{ ml: 'auto' }}>
                Select All
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* Content based on view mode */}
      <Paper>
        {viewMode === 'table' && <EnhancedHistoryTab entries={entries} title="" showFilters showExport />}
        {viewMode === 'timeline' && renderTimeline()}
        {viewMode === 'heatmap' && renderHeatmap()}
        {viewMode === 'metrics' && renderMetrics()}
      </Paper>
    </Box>
  );
};

export default HistoryTabPhase2;
